// =====================================================
// PAGE ADMIN: LISTE PRATICIENS
// =====================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Search, Filter, CheckCircle, XCircle, Clock, Eye, Stethoscope, ArrowUpDown, Edit, Save, User, Mail, Phone, MapPin, Briefcase, DollarSign, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { getPractitioners, updatePractitionerStatus } from '../../../services/psyPractitionerService';
import { supabase } from '../../../lib/supabase';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { useToast } from '../../../components/ui/use-toast';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { PractitionerModal } from '../../../components/admin/psy/PractitionerModal';

type SortField = 'name' | 'status' | 'consultations' | 'rating';
type SortDirection = 'asc' | 'desc';

export default function AdminPractitionersList() {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [filteredPractitioners, setFilteredPractitioners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedPractitioner, setSelectedPractitioner] = useState<any | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [practitionerToEdit, setPractitionerToEdit] = useState<any | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [practitionerToDelete, setPractitionerToDelete] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadPractitioners();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, statusFilter, practitioners, sortField, sortDirection]);

  const loadPractitioners = async () => {
    try {
      setLoading(true);
      // Pour admin: charger TOUS les praticiens (incluant pending, rejected, etc.)
      const data = await getPractitioners({ includeAll: true });
      setPractitioners(data);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Inverser la direction si même colonne
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouvelle colonne, commencer par asc
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const applyFilters = () => {
    let filtered = [...practitioners];

    // Recherche
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.verification_status === statusFilter);
    }

    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = (a.full_name || '').localeCompare(b.full_name || '');
          break;
        case 'status':
          const statusOrder = { pending: 1, approved: 2, rejected: 3, suspended: 4 };
          comparison = (statusOrder[a.verification_status as keyof typeof statusOrder] || 5) - 
                       (statusOrder[b.verification_status as keyof typeof statusOrder] || 5);
          break;
        case 'consultations':
          comparison = (a.total_consultations || 0) - (b.total_consultations || 0);
          break;
        case 'rating':
          comparison = (parseFloat(a.average_rating) || 0) - (parseFloat(b.average_rating) || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredPractitioners(filtered);
  };

  const handleUpdateStatus = async (practitionerId: string, status: string) => {
    try {
      await updatePractitionerStatus(practitionerId, status as any);
      toast({
        title: 'Succès',
        description: 'Statut mis à jour',
      });
      loadPractitioners();
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { className: string; label: string }> = {
      pending: { className: 'bg-yellow-400/20 text-yellow-400 border-yellow-400/20', label: 'En attente' },
      approved: { className: 'bg-green-400/20 text-green-400 border-green-400/20', label: 'Approuvé' },
      rejected: { className: 'bg-red-400/20 text-red-400 border-red-400/20', label: 'Rejeté' },
      suspended: { className: 'bg-orange-400/20 text-orange-400 border-orange-400/20', label: 'Suspendu' },
    };

    const badge = badges[status] || badges.pending;

    return (
      <Badge className={badge.className}>
        {badge.label}
      </Badge>
    );
  };

  const handleCreatePractitioner = () => {
    setPractitionerToEdit(null);
    setIsCreateModalOpen(true);
  };

  const handleEditPractitioner = (practitioner: any) => {
    setPractitionerToEdit(practitioner);
    setIsCreateModalOpen(true);
  };

  const handleModalClose = (refresh?: boolean) => {
    setIsCreateModalOpen(false);
    setPractitionerToEdit(null);
    if (refresh) {
      loadPractitioners();
    }
  };

  const handleViewDetails = (practitioner: any) => {
    setSelectedPractitioner(practitioner);
    setEditForm(practitioner);
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditForm(selectedPractitioner || {});
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedPractitioner) return;

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('psy_practitioners')
        .update({
          full_name: editForm.full_name,
          email: editForm.email,
          phone: editForm.phone,
          city: editForm.city,
          country: editForm.country,
          bio: editForm.bio,
          base_rate_per_session: editForm.base_rate_per_session,
          currency: editForm.currency,
          timezone: editForm.timezone,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedPractitioner.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Les informations du praticien ont été mises à jour',
      });

      await loadPractitioners();
      setSelectedPractitioner({ ...selectedPractitioner, ...editForm });
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (practitioner: any) => {
    setPractitionerToDelete(practitioner);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!practitionerToDelete) return;

    try {
      setIsDeleting(true);

      // Supprimer le praticien
      const { error } = await supabase
        .from('psy_practitioners')
        .delete()
        .eq('id', practitionerToDelete.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: `Le praticien ${practitionerToDelete.full_name} a été supprimé avec succès`,
      });

      // Recharger la liste
      await loadPractitioners();
      
      // Fermer le modal
      setShowDeleteConfirm(false);
      setPractitionerToDelete(null);
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de la suppression du praticien',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPractitionerToDelete(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center p-12">
          <Clock className="mr-2 h-8 w-8 animate-spin text-yellow-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Gestion des Praticiens</h1>
            <p className="text-gray-400">Vérifiez et approuvez les praticiens</p>
          </div>
          <Button
            onClick={handleCreatePractitioner}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau praticien
          </Button>
        </div>

        {/* Filtres */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par nom ou email..."
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>

            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-yellow-500/50"
              >
                <option value="all" className="bg-gray-900">Tous les statuts</option>
                <option value="pending" className="bg-gray-900">En attente</option>
                <option value="approved" className="bg-gray-900">Approuvés</option>
                <option value="rejected" className="bg-gray-900">Rejetés</option>
                <option value="suspended" className="bg-gray-900">Suspendus</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{practitioners.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">En attente</div>
            <div className="text-2xl font-bold text-yellow-400">
              {practitioners.filter((p) => p.verification_status === 'pending').length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Approuvés</div>
            <div className="text-2xl font-bold text-green-400">
              {practitioners.filter((p) => p.verification_status === 'approved').length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Rejetés</div>
            <div className="text-2xl font-bold text-red-400">
              {practitioners.filter((p) => p.verification_status === 'rejected').length}
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Praticien
                      <ArrowUpDown size={14} className={sortField === 'name' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Spécialités
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Statut
                      <ArrowUpDown size={14} className={sortField === 'status' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('consultations')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Consultations
                      <ArrowUpDown size={14} className={sortField === 'consultations' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredPractitioners.map((practitioner, index) => (
                  <tr key={practitioner.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden bg-blue-400/10">
                          {practitioner.photo_url ? (
                            <img
                              src={practitioner.photo_url}
                              alt={practitioner.full_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Stethoscope className="h-5 w-5 text-blue-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{practitioner.full_name}</div>
                          <div className="text-sm text-gray-400">{practitioner.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-white">
                        {practitioner.specialties?.slice(0, 2).join(', ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(practitioner.verification_status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">{practitioner.total_consultations || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPractitioner(practitioner)}
                          className="text-blue-400 hover:text-blue-300"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(practitioner)}
                          className="text-yellow-400 hover:text-yellow-300"
                          title="Voir les détails"
                        >
                          <Eye size={18} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(practitioner)}
                          className="text-red-400 hover:text-red-300"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </Button>

                        <Link to={`/psy/praticiens/${practitioner.id}`} target="_blank" rel="noopener noreferrer">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-400 hover:text-blue-300"
                            title="Page publique"
                          >
                            <ExternalLink size={18} />
                          </Button>
                        </Link>

                        {practitioner.verification_status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateStatus(practitioner.id, 'approved')}
                              className="text-green-400 hover:text-green-300"
                              title="Approuver"
                            >
                              <CheckCircle size={18} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateStatus(practitioner.id, 'rejected')}
                              className="text-red-400 hover:text-red-300"
                              title="Rejeter"
                            >
                              <XCircle size={18} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                </tr>
              ))}
              {filteredPractitioners.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400">
                    <Stethoscope className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p>Aucun praticien trouvé</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de détails/édition */}
      {showDetailsModal && selectedPractitioner && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#001122] to-[#000811] rounded-lg border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Stethoscope className="h-6 w-6 text-blue-400" />
                  Détails du Praticien
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations personnelles */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-400" />
                  Informations Personnelles
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Nom complet</label>
                    {isEditing ? (
                      <Input
                        value={editForm.full_name || ''}
                        onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Nom complet"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.full_name || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Statut</label>
                    <div>{getStatusBadge(selectedPractitioner.verification_status)}</div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm text-gray-400 block mb-2">Biographie</label>
                    {isEditing ? (
                      <textarea
                        value={editForm.bio || ''}
                        onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent min-h-[100px]"
                        placeholder="Biographie du praticien"
                      />
                    ) : (
                      <p className="text-white">{selectedPractitioner.bio || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-400" />
                  Contact
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Email</label>
                    {isEditing ? (
                      <Input
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="email@example.com"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.email || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Téléphone</label>
                    {isEditing ? (
                      <Input
                        type="tel"
                        value={editForm.phone || ''}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="+237 6XX XXX XXX"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.phone || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Ville</label>
                    {isEditing ? (
                      <Input
                        value={editForm.city || ''}
                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Ville"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.city || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Pays</label>
                    {isEditing ? (
                      <Input
                        value={editForm.country || ''}
                        onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Pays"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.country || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Fuseau horaire</label>
                    {isEditing ? (
                      <Input
                        value={editForm.timezone || ''}
                        onChange={(e) => setEditForm({ ...editForm, timezone: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Africa/Douala"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.timezone || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Tarification */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-blue-400" />
                  Tarification
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Tarif par session</label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editForm.base_rate_per_session || ''}
                        onChange={(e) => setEditForm({ ...editForm, base_rate_per_session: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="50000"
                      />
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.base_rate_per_session || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Devise</label>
                    {isEditing ? (
                      <select
                        value={editForm.currency || ''}
                        onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      >
                        <option value="" className="bg-gray-900">Sélectionner</option>
                        <option value="XAF" className="bg-gray-900">XAF (Franc CFA)</option>
                        <option value="EUR" className="bg-gray-900">EUR (Euro)</option>
                        <option value="USD" className="bg-gray-900">USD (Dollar)</option>
                      </select>
                    ) : (
                      <p className="text-white font-medium">{selectedPractitioner.currency || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Spécialités et Langues */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-400" />
                  Compétences
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Spécialités</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedPractitioner.specialties?.map((spec: string, idx: number) => (
                        <Badge key={idx} className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          {spec}
                        </Badge>
                      )) || <p className="text-gray-400">Aucune</p>}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Langues</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedPractitioner.languages?.map((lang: string, idx: number) => (
                        <Badge key={idx} className="bg-green-500/20 text-green-400 border-green-500/30">
                          {lang}
                        </Badge>
                      )) || <p className="text-gray-400">Aucune</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistiques */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-400" />
                  Statistiques
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-3xl font-bold text-blue-400">
                      {selectedPractitioner.total_consultations || 0}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">Consultations</div>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-3xl font-bold text-yellow-400">
                      {selectedPractitioner.average_rating || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">Note moyenne</div>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-sm text-gray-400">Licence</div>
                    <div className="text-white font-medium mt-1">
                      {selectedPractitioner.license_number || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex justify-between">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleCancelEdit}
                    variant="outline"
                    className="border-white/10 text-gray-300 hover:bg-white/5"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:opacity-90"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Enregistrer
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleEdit}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </Button>
                    <Link to={`/psy/praticiens/${selectedPractitioner.id}`} target="_blank" rel="noopener noreferrer">
                      <Button
                        variant="outline"
                        className="border-blue-400 text-blue-400 hover:bg-blue-400/10"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Page publique
                      </Button>
                    </Link>
                  </div>
                  <Button
                    onClick={() => setShowDetailsModal(false)}
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:opacity-90"
                  >
                    Fermer
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de création/édition */}
      <PractitionerModal
        isOpen={isCreateModalOpen}
        onClose={handleModalClose}
        practitioner={practitionerToEdit}
      />

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && practitionerToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg shadow-xl max-w-md w-full border border-white/10">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-500/10 rounded-lg">
                  <Trash2 className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Confirmer la suppression</h3>
                  <p className="text-sm text-gray-400">Cette action est irréversible</p>
                </div>
              </div>

              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <p className="text-white mb-2">
                  Êtes-vous sûr de vouloir supprimer le praticien :
                </p>
                <p className="text-lg font-semibold text-blue-400">
                  {practitionerToDelete.full_name}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {practitionerToDelete.email}
                </p>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-6">
                <p className="text-sm text-red-300">
                  ⚠️ Attention : Toutes les données associées à ce praticien seront supprimées (consultations, disponibilités, etc.)
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  variant="outline"
                  className="flex-1 border-white/10 text-gray-300 hover:bg-white/5"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white hover:opacity-90"
                >
                  {isDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Suppression...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Supprimer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
}
